import Head from "next/head";
import React, { useState, useEffect } from "react";
import ReactCSSTransition from "react-addons-css-transition-group";
import { NextPage } from "next";
import { setTimeout } from "timers";

import Card from "../components/Card";
import ContactForm from "../components/ContactForm.js";
import api from "../lib/api";
import wp, { WordPressPost } from "../lib/wp";
import { socialMetadata } from "src/components/layout/Head";
import { trackEvent } from "src/lib/analytics";
import DiscordWidget from "src/components/DiscordWidget";
import { IPageProps } from "./_app";

// Constants
const pageID = 46;

interface IContactPageProps extends IPageProps {
  page: WordPressPost;
}

interface IContactPageState {
  cards: any[];
  currentCard: number;
  contactFormSubmitted: boolean;
  showThankYou: boolean;
  page: any;
}

const ContactPage: NextPage<IContactPageProps> = ({ page, signup, user }) => {
  const [pageState, setPageState] = useState<IContactPageState>({
    cards: [
      {
        title: "General Enquiry",
        body:
          "<p>For general enquiries, including those relating to partnerships, investement, and press/media.</p>",
        selected: false,
      },
      {
        title: "Sales Question",
        body:
          "<p>We'd love to talk about how we can work together, and how HASH can help.</p>",
        selected: false,
      },
      {
        title: "Help & Support",
        body:
          "<p>Product support and help using HASH. <i>You can also use the chat button in-app any time.</i></p>",
        selected: false,
      },
    ],
    currentCard: -1,
    contactFormSubmitted: false,
    showThankYou: false,
    page: null,
  });

  const parseWP = (parsedPage: any) => {
    if (
      parsedPage.includes(`<div class="wp-block-columns has-3-columns cards">`)
    ) {
      const startIndex = parsedPage.indexOf(
        `<div class="wp-block-columns has-3-columns cards">`
      );

      const tags = [];
      let tag;
      for (let c = startIndex; c < parsedPage.length; c++) {
        if (parsedPage[c] === "<") {
          tag = parsedPage.substring(
            c,
            c + parsedPage.substring(c).indexOf(">") + 1
          );
          tags.push(tag);
          // Check if this is the end of the cards tag
          if (tag === "</div>" && tags.length % 2 === 0) {
            break;
          }
        }
      }
    }
    setPageState({ ...pageState, page: parsedPage });
  };

  useEffect(() => {
    parseWP(page.content.rendered);
  }, []);

  let contactForm: any = null;

  const handleInput = (type: any, input: any) => {
    switch (type) {
      case "card":
        const newCards = pageState.cards;
        for (const card of newCards) {
          card.selected = false;
        }
        newCards[input].selected = true;
        setPageState({ ...pageState, cards: newCards, currentCard: input });

        setTimeout(
          () =>
            contactForm?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            }),
          50
        );

        break;
      case "contact-form":
        submitContactForm(input);
        break;
      default:
        break;
    }
  };
  const submitContactForm = (submission: any) => {
    trackEvent(
      {
        category: "Growth",
        action: "Contact Form Submitted",
      },
      user
    );
    // Sending form to backend
    api
      .post(
        `/contact/message`,
        {},
        {
          // The submission
          submission,
          // The submission type
          type: pageState.cards[pageState.currentCard].title,
          // User's id if logged in
          hash_id: user?.id,
        }
      )
      .then(() => {
        setPageState({ ...pageState, contactFormSubmitted: true });
        setTimeout(() => {
          setPageState({ ...pageState, showThankYou: true });
        }, 300);
      });
  };

  const cards = [];
  for (let i = 0; i < pageState.cards.length; i++) {
    cards.push(
      <Card
        key={i}
        selected={pageState.cards[i].selected}
        title={pageState.cards[i].title}
        body={pageState.cards[i].body}
        onClick={() => handleInput("card", i)}
        color={i === 1 ? "pink" : i === 2 ? "blue" : "purple"}
      />
    );
  }

  const fields = [
    {
      label: "Your first name",
      name: "firstName",
      required: true,
      valid: (input: any) => (input.length > 0 ? true : false),
    },
    {
      label: "Your last name",
      name: "lastName",
      required: true,
      valid: (input: any) => (input.length > 0 ? true : false),
    },
    {
      label: "Your work email",
      name: "workEmail",
      type: "email",
      required: true,
      valid: (input: any) =>
        input.match(/[a-z0-9\.\_\%\+\-]+@[a-z0-9\.\-]+\.[a-z]{2,}$/g)
          ? true
          : false,
    },
    {
      label: "Company website",
      name: "companyWebsite",
    },
    {
      label: "Country",
      name: "country",
    },
    {
      label: "Your message",
      name: "message",
      size: "big",
      required: true,
      valid: (input: any) => (input.length > 0 ? true : false),
    },
  ];
  return (
    <>
      <Head>
        {socialMetadata({
          title: "Contact - HASH",
          url: "https://hash.ai/contact",
        })}
      </Head>
      <div className="contact-page">
        <div className="mainsite-header">
          <div className="container">
            <h1 className="heading-1">Contact</h1>
            <p className="large-text">Find out more about getting in touch</p>
          </div>
        </div>
        <div className="container contact-links">
          <p>
            <span>You can chat to us right now on </span>
            <DiscordWidget signup={signup} textLink />
            <span> or send us a message </span>
            <a onClick={() => window.zE?.("webWidget", "open")}>here</a>.
          </p>
          <p>
            Alternatively, fill out the form below and we'll get back to
            you as soon as possible.
          </p>
        </div>
        <div className="container">
          <div className="row">
            <div className="col margin-top">
              {pageState.currentCard != null && pageState.showThankYou ? (
                <div className="thank-you">
                  <h3 className="no-margin-top">
                    Thank you for your submission
                  </h3>
                  <p>We'll get back to you as soon as possible</p>
                </div>
              ) : (
                <div>
                  <h3 className="no-margin-top">How can we help you?</h3>
                  <div className="cards margin-bottom">{cards}</div>
                  <div
                    ref={(el) => (contactForm = el)}
                    className={
                      pageState.currentCard != null
                        ? pageState.showThankYou
                          ? "thank-you form-spacing"
                          : "form-spacing"
                        : ""
                    }
                  >
                    <ReactCSSTransition
                      transitionName="alert"
                      transitionEnterTimeout={200}
                      transitionLeaveTimeout={200}
                    >
                      {pageState.currentCard !== -1 && !pageState.showThankYou && (
                        <div
                          className={
                            pageState.contactFormSubmitted ? "submitted" : ""
                          }
                        >
                          <h3 className="no-margin-top">
                            Please let us know a little bit more...
                          </h3>
                          <ContactForm
                            submit={(submission: any) =>
                              handleInput("contact-form", submission)
                            }
                            submitted={pageState.contactFormSubmitted}
                            fields={fields}
                          />
                        </div>
                      )}
                    </ReactCSSTransition>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

ContactPage.getInitialProps = async () => {
  let page;
  try {
    page = await wp.pages().id(pageID);
  } catch (err) {
    return err;
  }
  return { page };
};

export default ContactPage;
